import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, BriefcaseIcon, ArrowRightIcon } from 'lucide-react';
import { PortraitFrame } from '../components/PortraitFrame';
import { SectionDirectory } from '../components/SectionDirectory';
import { profile } from '../data/profile';
const fade = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.28,
      delay: index * 0.05,
      ease: [0.23, 1, 0.32, 1] as const
    }
  })
};
export function BoardPositioning() {
  return <div className="bg-white" data-id="element-208" data-mp="pw1w8nxjrk9t">
      <section className="relative overflow-hidden bg-navy-900 pb-20 pt-28 lg:pb-28 lg:pt-36" aria-labelledby="hero-heading" data-id="element-209" data-mp="pv08bivfn3ao">
        <div aria-hidden="true" className="absolute inset-y-0 right-0 hidden w-[38%] border-l border-white/5 bg-navy-800 lg:block" data-id="element-210" data-mp="uwzdmyaqgaa9" />

        <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:gap-14 lg:px-10" data-id="element-211" data-mp="3fm569ier6lj4">
          <div className="lg:col-span-7" data-id="element-212" data-mp="qu2g35y0ccav">
            <motion.p custom={0} initial="hidden" animate="visible" variants={fade} className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold-400" data-id="element-213" data-mp="ltxzzh10hjl6">
              Introduction · Executive Hero &amp; Board Positioning
            </motion.p>

            <motion.h1 id="hero-heading" custom={1} initial="hidden" animate="visible" variants={fade} className="mt-5 font-display text-5xl font-extrabold leading-[1.02] tracking-tightest text-white sm:text-6xl xl:text-7xl" data-id="element-214" data-mp="19p0lpg4ir6xf">
              {profile.name}
            </motion.h1>

            <motion.p custom={2} initial="hidden" animate="visible" variants={fade} className="mt-5 max-w-2xl font-display text-lg font-semibold leading-snug text-platinum-200 sm:text-xl" data-id="element-215" data-mp="1si4j9ckuy3xb">
              {profile.title}
            </motion.p>

            <motion.div custom={3} initial="hidden" animate="visible" variants={fade} className="mt-7 max-w-2xl border-l-2 border-gold-500 pl-5" data-id="element-216" data-mp="20g6ehembysao">
              <p className="font-sans text-base leading-relaxed text-platinum-300 sm:text-lg" data-id="element-217" data-mp="2ea785tyfdp46">
                {profile.valueStatement}
              </p>
            </motion.div>

            <motion.dl custom={4} initial="hidden" animate="visible" variants={fade} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3" data-id="element-218" data-mp="2o7vg5ll1kt4a">
              <div className="flex items-center gap-2" data-id="element-219" data-mp="tum3jkx78rht">
                <MapPinIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-220" data-mp="tw6xp9flq8f5" />
                <dt className="sr-only" data-id="element-221" data-mp="yv1ek5u0dmwm">Location</dt>
                <dd className="font-sans text-sm text-platinum-200" data-id="element-222" data-mp="3c3gkqm0cpi3n">{profile.location}</dd>
              </div>
              <div className="flex items-center gap-2" data-id="element-223" data-mp="37bfbl8yhc7hu">
                <BriefcaseIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-224" data-mp="33awm3lk27dit" />
                <dt className="sr-only" data-id="element-225" data-mp="2ybscitt83dry">Seniority</dt>
                <dd className="font-sans text-sm text-platinum-200" data-id="element-226" data-mp="2yd3ceijjs1ds">{profile.seniority}</dd>
              </div>
            </motion.dl>

            <motion.div custom={5} initial="hidden" animate="visible" variants={fade} className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-227" data-mp="3uzqai6km7x3h">
              <Link to="/about" className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-gold-500 px-6 py-3.5 font-sans text-sm font-semibold text-navy-950 transition-colors duration-150 ease-out hover:bg-gold-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-228" data-mp="3h2tbhhucf9bi">
                Executive Profile
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-229" data-mp="26px2xqth91x" />
              </Link>
              <Link to="/board-education" className="inline-flex items-center justify-center whitespace-nowrap border border-white/25 px-6 py-3.5 font-sans text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-230" data-mp="3pg4pgb1ud2ez">
                Board &amp; Governance Credentials
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center whitespace-nowrap px-6 py-3.5 font-sans text-sm font-semibold text-platinum-200 underline decoration-gold-500 decoration-2 underline-offset-8 transition-colors duration-150 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-231" data-mp="2rt5rzy3brqg1">
                Contact Yolandi
              </Link>
            </motion.div>
          </div>

          <motion.div custom={2} initial="hidden" animate="visible" variants={fade} className="lg:col-span-5" data-id="element-232" data-mp="25ls65imd0cgn">
            <PortraitFrame data-id="element-233" data-mp="21in57khxgiu4" />
          </motion.div>
        </div>
      </section>

      <section className="border-y border-navy-700 bg-navy-950 py-14" aria-label="Guiding principle" data-id="element-234" data-mp="2fd63bjqu906m">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-10" data-id="element-235" data-mp="2aj1ram2pu1yo">
          <figure className="mx-auto max-w-4xl text-center" data-id="element-236" data-mp="1muimigivk7bc">
            <blockquote data-id="element-237" data-mp="1hwqs9furc93z">
              <p className="font-display text-2xl font-semibold leading-snug tracking-tight text-white sm:text-3xl" data-id="element-238" data-mp="1hx8uqu80jjkc">
                “{profile.quote}”
              </p>
            </blockquote>
            <figcaption className="mt-6 font-sans text-sm uppercase tracking-[0.18em] text-gold-400" data-id="element-239" data-mp="3mx1n9tyoil11">
              Financial Services · Energy &amp; Critical Infrastructure · Professional Services
            </figcaption>
          </figure>
        </div>
      </section>

      <SectionDirectory data-id="element-240" data-mp="2yzukeb89i26k" />
    </div>;
}